import * as PIXI from "pixi.js-legacy"

import TextManager from "./TextManager"
import InteractionManager from "./InteractionManager"
import ResourceManager from "./ResourceManager"
import Story from "./Story"

import * as constants from "./constants"
import * as Utils from "./utils"

export default class Player {

	// The window size is the full screen size
	get windowSize() { return this._windowSize }

	// The PIXI application's canvas follows the viewport ratio constraint
	get viewportSize() { return this._viewportSize }

	// The rootElement is the parent DOM element (HTML page's body)
	constructor(rootElement) {
		this._rootElement = rootElement

		// Create the PIXI application with a default background color
		this._app = new PIXI.Application({
			backgroundColor: constants.defaultBackgroundColor,
			resolution: window.devicePixelRatio, // Improves resolution on Retina displays
		})

		// Add the PIXI app's canvas to the DOM
		this._app.stage.name = "stageContainer"
		this._rootElement.appendChild(this._app.view)

		// Create the PIXI container that will hold the loading message
		this._textManager = new TextManager(this, this._app.stage)

		// Create the PIXI container that will hold the visual content (story pages)
		this._contentContainer = new PIXI.Container()
		this._contentContainer.name = "contentContainer"
		this._app.stage.addChild(this._contentContainer)

		// Size the viewport (based on window size, before any story data is read)
		this._app.renderer.autoDensity = true
		this._resize()

		// Create the interaction manager (which will deal with user gestures)
		this._interactionManager = new InteractionManager(this, this._rootElement)

		// Initialize story data (the story will handle the DiViNa data)
		this._story = null
		this._minRatio = null
		this._maxRatio = null

		// Add resize event listener
		window.addEventListener("resize", this._resize.bind(this))
	}

	// The _resize function is called on creating the Player
	// and on any _resize call (e.g. after an orientation change)
	_resize() {
		const { width, height } = this._rootElement.getBoundingClientRect()
		this._windowSize = { width, height }
		// Size viewport based on window size (and applicable viewport ratio constraints)
		this._sizeViewport()

		// Now resize _textManager and _story if they exist
		if (this._textManager) {
			this._textManager.resize()
		}
		if (this._story) {
			this._story.resize()
		}
	}

	// _sizeViewport sizes the viewport based on window size (and viewport ratio constraints)
	_sizeViewport() {
		const { width, height } = this._windowSize
		let viewportWidth = width
		let viewportHeight = height

		// Get the (target) ratio value that conforms to the viewport ratio constraints
		const applicableRatio = this._getApplicableRatio()
		const windowRatio = width / height

		if (windowRatio >= applicableRatio) {
			// The window's height becomes the viewport's and constrains the viewport's width
			viewportWidth = height * applicableRatio

			// Center the canvas
			if (!this._app.renderer) {
				return
			}
			this._app.renderer.view.style.marginLeft = `${(width - viewportWidth) / 2}px`
			this._app.renderer.view.style.marginTop = "0px"

		} else if (windowRatio < applicableRatio) {
			// The window's width becomes the viewport's and constrains the viewport's height
			viewportHeight = width / applicableRatio

			// Center the canvas
			if (!this._app.renderer) {
				return
			}
			this._app.renderer.view.style.marginLeft = "0px"
			this._app.renderer.view.style.marginTop = `${(height - viewportHeight) / 2}px`
		}

		// Store the viewport size's value
		this._viewportSize = {
			width: viewportWidth,
			height: viewportHeight,
		}

		// Resize the canvas using PIXI's built-in function
		this._app.renderer.resize(viewportWidth, viewportHeight)

		// Position the contentContainer's pivot to ensure that the containers added to it
		// (as children) will have their (0, 0) point at the center of the window/viewport
		this._contentContainer.pivot = {
			x: -viewportWidth / 2,
			y: -viewportHeight / 2,
		}
	}

	// _getApplicableRatio computes the (target) ratio that conforms to the viewport ratio constraints
	// Reminder: _viewportRatio = { min, max } where both min and max are written as "width:height"
	// (e.g. "3:4" for a 4/3 ratio in portrait mode)
	_getApplicableRatio() {
		// Define a default ratio as the current window ratio
		const { width, height } = this._windowSize
		const currentRatio = width / height

		// If there are no viewportRatio constraints, then keep the current window ratio
		if (!this._minRatio && !this._maxRatio) {
			return currentRatio
		}

		// If there's only a min, or only a max, then apply the constraint
		if (this._minRatio && !this._maxRatio) {
			return Math.max(this._minRatio, currentRatio)
		}
		if (this._maxRatio && !this._minRatio) {
			return Math.min(this._maxRatio, currentRatio)
		}

		// If both a min and max are defined, then apply both constraints
		return Math.min(Math.max(this._minRatio, currentRatio), this._maxRatio)
	}

	// _setRatioConstraint is called by the _story once the DiViNa data has been read
	_setRatioConstraint(viewportRatio) {
		if (!viewportRatio) {
			return
		}

		// Parse viewportRatio properties to compute the applicable min and max ratio
		const { min, max } = viewportRatio
		let minRatio = Utils.parseMinOrMaxRatio(min)
		let maxRatio = Utils.parseMinOrMaxRatio(max)

		// If the min and max values are contradictory, then discard them
		if (minRatio && maxRatio && minRatio > maxRatio) {
			minRatio = null
			maxRatio = null
		}

		// Now store those min and max values and resize the viewport
		this._minRatio = minRatio
		this._maxRatio = maxRatio
		this._resize()
	}

	// For loading the DiViNa data from a folder path
	openDiViNaFromPath(folderPath, doOnCenterTap) {
		// Set the action to trigger on center tap
		this._interactionManager.setOnCenterTapAction(doOnCenterTap)

		// Create resource manager and story
		this._createResourceManager()
		this._createStory()

		// Load story from folder path
		this._story.loadFromPath(folderPath)
	}

	_createResourceManager(base64DataByHref = null) {
		this._resourceManager = new ResourceManager(this._textManager, base64DataByHref)
	}

	_createStory() {
		this._story = new Story(this, this._textManager, this._interactionManager,
			this._resourceManager, this._contentContainer, this._setRatioConstraint.bind(this))
	}

	// For loading the DiViNa data from an object = { json, base64DataByHref }
	openDiViNaFromData(data) {
		// Create resource manager and story
		this._createResourceManager(data.base64DataByHref)
		this._createStory()

		// Load story from data
		this._story.loadFromData(data)
	}

	// For getting rid of the _textManager
	_removeTextManager() {
		this._textManager.destroy()
		this._textManager = null
	}

	// For accessing a point in the story (i.e. a linkObject) from the table of contents
	goTo(href) {
		if (this._story) {
			this._story.goTo(href)
		}
	}

	// For exiting the application
	destroy() {
		window.removeEventListener("resize", this._resize.bind(this))
		this._story.destroy()
		this._resourceManager.destroy()
		this._interactionManager.destroy()
		if (this._textManager) {
			this._textManager.destroy()
		}
		const removeView = true
		this._app.destroy(removeView, { children: true, texture: false, baseTexture: false })
	}

}